import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Settings, 
  User, 
  Shield, 
  Bell, 
  Database,
  Globe,
  Key,
  Save,
  Plus,
  Trash2,
  Edit,
  Lock
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'super_admin' | 'admin' | 'viewer';
  status: 'active' | 'inactive';
  isAppOwner: boolean;
  lastLoginAt: string;
  createdAt: string;
}

const mockAdminUsers: AdminUser[] = [
  {
    id: '1',
    email: 'rongqi.chen@moatcity.com',
    firstName: 'Rongqi',
    lastName: 'Chen',
    role: 'super_admin',
    status: 'active',
    isAppOwner: true,
    lastLoginAt: '2024-08-13T14:30:00Z',
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    email: 'admin@argent.com',
    firstName: 'Sarah',
    lastName: 'Wilson',
    role: 'admin',
    status: 'active',
    isAppOwner: false,
    lastLoginAt: '2024-08-13T08:15:00Z',
    createdAt: '2024-03-01T00:00:00Z'
  },
  {
    id: '3',
    email: 'ceo@urbanprojects.com',
    firstName: 'Emma',
    lastName: 'Davis',
    role: 'admin',
    status: 'inactive',
    isAppOwner: false,
    lastLoginAt: '2024-07-28T12:00:00Z',
    createdAt: '2024-01-01T00:00:00Z'
  }
];

const AdminSettings: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>(mockAdminUsers);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'admin' as const
  });
  
  // Password management states
  const [showResetPassword, setShowResetPassword] = useState<string | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [changePassword, setChangePassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Check if current user is app owner (this should come from auth context)
  const [isAppOwner, setIsAppOwner] = useState(false);
  
  // Check current user's app owner status from session
  React.useEffect(() => {
    const checkAdminUser = async () => {
      try {
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          console.error('No admin token found - redirecting to login');
          window.location.href = '/admin/login';
          return;
        }

        const response = await fetch(`${CONFIG.API_BASE_URL}/api/admin/user`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAppOwner(data.user?.isAppOwner || false);
        } else if (response.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
          return;
        }
      } catch (error) {
        console.error('Error checking admin user:', error);
      }
    };

    checkAdminUser();
  }, []);

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    emailNotifications: true,
    auditLogging: true,
    autoBackup: true,
    sessionTimeout: '24',
    maxLoginAttempts: '5'
  });

  const getRoleBadge = (role: string, isAppOwner: boolean) => {
    if (isAppOwner) {
      return <Badge className="bg-red-100 text-red-800">App Owner</Badge>;
    }
    
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-purple-100 text-purple-800">Super Admin</Badge>;
      case 'admin':
        return <Badge className="bg-blue-100 text-blue-800">Admin</Badge>;
      case 'viewer':
        return <Badge className="bg-gray-100 text-gray-800">Viewer</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const addAdminUser = () => {
    const user: AdminUser = {
      id: Date.now().toString(),
      ...newUser,
      status: 'active',
      isAppOwner: false,
      lastLoginAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    
    setAdminUsers(prev => [...prev, user]);
    setNewUser({
      email: '',
      firstName: '',
      lastName: '',
      role: 'admin'
    });
    setShowAddUser(false);
  };

  const resetUserPassword = async (userId: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ newPassword: resetPassword })
      });

      if (response.ok) {
        alert('Password reset successfully');
        setShowResetPassword(null);
        setResetPassword('');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const changeOwnPassword = async () => {
    if (changePassword.newPassword !== changePassword.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    try {
      const response = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: changePassword.currentPassword,
          newPassword: changePassword.newPassword
        })
      });

      if (response.ok) {
        alert('Password changed successfully');
        setShowChangePassword(false);
        setChangePassword({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password');
    }
  };

  const saveSystemSettings = () => {
    // Mock save functionality
    console.log('Saving system settings:', systemSettings);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage system configuration and admin users</p>
        </div>
        <Button onClick={() => setShowChangePassword(true)}>
          <Lock className="w-4 h-4 mr-2" />
          Change Password
        </Button>
      </div>

      {/* System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            System Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">General</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Maintenance Mode</label>
                  <p className="text-sm text-gray-500">Temporarily disable the platform</p>
                </div>
                <Switch
                  checked={systemSettings.maintenanceMode}
                  onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, maintenanceMode: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Email Notifications</label>
                  <p className="text-sm text-gray-500">Send email notifications to users</p>
                </div>
                <Switch
                  checked={systemSettings.emailNotifications}
                  onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, emailNotifications: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Audit Logging</label>
                  <p className="text-sm text-gray-500">Log all administrative actions</p>
                </div>
                <Switch
                  checked={systemSettings.auditLogging}
                  onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, auditLogging: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Auto Backup</label>
                  <p className="text-sm text-gray-500">Automatically backup data daily</p>
                </div>
                <Switch
                  checked={systemSettings.autoBackup}
                  onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, autoBackup: checked }))}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">Security</h3>
              
              <div>
                <label className="text-sm font-medium">Session Timeout (hours)</label>
                <Select value={systemSettings.sessionTimeout} onValueChange={(value) => setSystemSettings(prev => ({ ...prev, sessionTimeout: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="4">4 hours</SelectItem>
                    <SelectItem value="8">8 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Max Login Attempts</label>
                <Select value={systemSettings.maxLoginAttempts} onValueChange={(value) => setSystemSettings(prev => ({ ...prev, maxLoginAttempts: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 attempts</SelectItem>
                    <SelectItem value="5">5 attempts</SelectItem>
                    <SelectItem value="10">10 attempts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveSystemSettings}>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">Reset User Password</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">New Password</label>
                <Input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={() => resetUserPassword(showResetPassword)}
                  disabled={!resetPassword}
                >
                  Reset Password
                </Button>
                <Button variant="outline" onClick={() => setShowResetPassword(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Own Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">Change Your Password</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Current Password</label>
                <Input
                  type="password"
                  value={changePassword.currentPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">New Password</label>
                <Input
                  type="password"
                  value={changePassword.newPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
                <Input
                  type="password"
                  value={changePassword.confirmPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={changeOwnPassword}
                  disabled={!changePassword.currentPassword || !changePassword.newPassword || !changePassword.confirmPassword}
                >
                  Change Password
                </Button>
                <Button variant="outline" onClick={() => setShowChangePassword(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Users Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              Admin Users
            </CardTitle>
            {isAppOwner && (
              <Button onClick={() => setShowAddUser(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Admin User
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Add User Form */}
          {showAddUser && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="text-lg font-medium mb-4">Add New Admin User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">First Name</label>
                  <Input
                    value={newUser.firstName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Name</label>
                  <Input
                    value={newUser.lastName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email address"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Role</label>
                  <Select value={newUser.role} onValueChange={(value: any) => setNewUser(prev => ({ ...prev, role: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex space-x-2 mt-4">
                <Button onClick={addAdminUser} disabled={!newUser.email || !newUser.firstName || !newUser.lastName}>
                  Add User
                </Button>
                <Button variant="outline" onClick={() => setShowAddUser(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback className="text-xs">
                            {getInitials(user.firstName, user.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.firstName} {user.lastName}</div>
                          <div className="text-sm text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role, user.isAppOwner)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {formatDate(user.lastLoginAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {formatDate(user.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {isAppOwner && !user.isAppOwner && (
                          <>
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setShowResetPassword(user.id)}
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {!isAppOwner && (
                          <span className="text-sm text-gray-500">Read Only</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="w-5 h-5 mr-2" />
              System Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Platform Version</span>
              <span className="text-sm font-medium">v2.1.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Database</span>
              <span className="text-sm font-medium">PostgreSQL 15.4</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Last Backup</span>
              <span className="text-sm font-medium">2 hours ago</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Uptime</span>
              <span className="text-sm font-medium">99.8%</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              Environment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Environment</span>
              <Badge className="bg-green-100 text-green-800">Production</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Region</span>
              <span className="text-sm font-medium">Europe (London)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">SSL Certificate</span>
              <Badge className="bg-green-100 text-green-800">Valid</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">API Status</span>
              <Badge className="bg-green-100 text-green-800">Healthy</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
